import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Modal} from 'react-bootstrap';
import PrivacyPolicy from '../../pages/Feature/Components/PrivacyPolicy';
import PrivacyPolicySelectionModal from './PrivacyPolicySelectionModal';
import './PrivacyPolicyConsentModal.scss';
import {TelService} from "../../services/TelService";
import {v4 as uuidv4} from "uuid";
import {Notification} from 'components';
import {FormattedMessage, useIntl} from 'react-intl';

const PrivacyPolicyConsentModal = ({
                                       isOpenModal,
                                       closeModal,
                                       onAgree,
                                       policyLink,
                                       onPolicyLinkClick,
                                       msisdn,
                                       logOut,
                                       flow,
                                   }) => {
    const [isOpenSelectionModal, setIsOpenSelectionModal] = useState(false);
    const [consentItems, setConsentItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [termId, setTermId] = useState();
    const [isFirst, setIsFirst] = useState(true);
    const intl = useIntl();

    const subscriberMsisdn = useMemo(() => msisdn || localStorage.getItem('mobile') || '', [msisdn]);

    const loadConsentItems = () => {
        setIsLoading(true);
        // setErrorMsg('');

        TelService.getTerms({}, (response) => {
            if (response?.errorCode !== '0') {
                // setErrorMsg(response?.message || 'Khong tai duoc danh sach muc dich xu ly du lieu.');
                setIsLoading(false);
                return;
            }

            // const list = normalizeConsentItems(response);
            const list = response.result?.[0]?.items;
            setConsentItems(list);
            setTermId(response.result?.[0]?.termId);
            if (isFirst) {
                setSelectedIds(list.map((item) => item.itemId));
            }
            setIsLoading(false);
        }, (error) => {
            // setErrorMsg(error?.message || 'Không tải được danh sách mục đích xử lý dữ liệu.');
            setIsLoading(false);
        });
    };

    useEffect(() => {
        if (isOpenModal) {
            loadConsentItems();
            setIsFirst(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpenModal]);

    const handlePolicyClick = (event) => {
        event.preventDefault();

        if (onPolicyLinkClick) {
            onPolicyLinkClick(event);
        }

        // Avoid stacked overlays by switching from the short policy modal to the full consent modal.
        closeModal();
        setIsOpenSelectionModal(true);
    };

    const buildConsentPayload = (consentSelectedIds) => {
        const terms = {};
        for (const consent of consentItems) {
            terms[consent?.itemCode] = consentSelectedIds.includes(consent.itemId) ? 1 : 0;
        }

        return {
            requestId: uuidv4(),
            msisdn: subscriberMsisdn,
            termId,
            channel_id: 'web',
            terms,
        };
    };

    const handleConfirm = (nextSelectedIds) => {
        const consentSelectedIds = Array.isArray(nextSelectedIds) ? nextSelectedIds : selectedIds;
        setSelectedIds(consentSelectedIds);

        if (flow === 'login') {
            const payload = buildConsentPayload(consentSelectedIds);
            setIsSaving(true);
            TelService.saveConsent(payload, (response) => {
                if (response?.errorCode !== '0') {
                    Notification.error(response?.message || 'Cập nhật xác nhận thất bại.');
                    setIsSaving(false);
                    return;
                }
                Notification.success(response?.message || 'Cập nhật xác nhận thành công.');
                setIsSaving(false);

                if (onAgree) {
                    onAgree();
                }
                closeModal();
                setIsOpenSelectionModal(false);
            }, (error) => {
                Notification.error(error?.message || 'Cập nhật xác nhận thất bại.');
                setIsSaving(false);
            });
        } else if (flow === 'register') {
            if (onAgree) {
                onAgree(buildConsentPayload(consentSelectedIds));
            }
            closeModal();
            setIsOpenSelectionModal(false);
        }
    };

    return (
        <>
            <Modal
                show={isOpenModal}
                centered
                dialogClassName="privacy-policy-consent-modal"
            >
                <Modal.Body>
                    <button
                        type="button"
                        aria-label={intl.formatMessage({id: 'IDS_BTN_CLOSE'})}
                        onClick={() => {
                            if (flow === 'login') {
                                logOut();
                            }
                            closeModal();
                        }}
                        className="privacy-policy-consent-modal__close-btn"
                    >
                        ×
                    </button>
                    <div className="privacy-policy-consent-modal__policy-wrapper">
                        <PrivacyPolicy showDocuments={false}/>
                    </div>

                    <p className="privacy-policy-consent-modal__note">
                        <FormattedMessage
                            id="LOGIN_42"
                            values={{
                                detail: (chunks) => (
                                    <a
                                        href={policyLink}
                                        onClick={handlePolicyClick}
                                        className="privacy-policy-consent-modal__link"
                                    >
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        onClick={handleConfirm}
                        className="privacy-policy-consent-modal__agree-btn"
                        disabled={isSaving || isLoading}
                    >
                        {isSaving
                            ? <FormattedMessage id="IDS_TV_DANG_XU_LY"/>
                            : <FormattedMessage id="LOGIN_46"/>}
                    </Button>
                </Modal.Footer>
            </Modal>

            <PrivacyPolicySelectionModal
                isOpenModal={isOpenSelectionModal}
                closeModal={() => setIsOpenSelectionModal(false)}
                closeModalLogin={onAgree}
                listConsent={consentItems}
                msisdn={msisdn}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                logOut={logOut}
                handleConfirm={handleConfirm}
                isSaving={isSaving}
                flow={flow}
            />
        </>
    );
};

PrivacyPolicyConsentModal.propTypes = {
    isOpenModal: PropTypes.bool,
    closeModal: PropTypes.func,
    onAgree: PropTypes.func,
    policyLink: PropTypes.string,
    onPolicyLinkClick: PropTypes.func,
    msisdn: PropTypes.string,
    logOut: PropTypes.func,
    flow: PropTypes.string,
};

PrivacyPolicyConsentModal.defaultProps = {
    isOpenModal: false,
    closeModal: () => {
    },
    onAgree: null,
    policyLink: '/chinh-sach-rieng-tu',
    onPolicyLinkClick: null,
    msisdn: '',
    logOut: () => {
    },
    flow: ''
};

// export default React.memo(PrivacyPolicyConsentModal);
export default PrivacyPolicyConsentModal;
